/* Ayudate · Solicitud de tutoría */

import React, { useState, useMemo, useEffect } from "react";
import { Capa } from "./Capa";
import { Avatar } from "./Avatar";
import { AvisoPoliticas, PanelPoliticas } from "./Politicas";
import { ListaErrores, Ayuda, NotaCorreo } from "./Mensajes";
import { TelefonoInput } from "./TelefonoInput";
import { infoCurso } from "../datos/areas";
import { colones } from "../lib/formato";
import { tarifas, r100 } from "../lib/tarifas";
import { DIAS, HORAS, HORAS_MINIMAS, DIAS_MAXIMO_RESERVA, hLabel } from "../lib/horarios";
import { vNombre, vCorreo, vTelefono, vTexto } from "../lib/validar";
import { irA_ruta } from "../lib/rutas";
import { leerSesionEstudiante } from "../lib/sesionEstudiante";
import { CODIGO_PAIS_PREDETERMINADO } from "../datos/codigosPais";
import { api } from "../api";

/* Bloqueo temporal: mientras el estudiante tiene horas elegidas, se las
   "reserva" 5 minutos para que no se dupliquen envíos ni otro estudiante
   la tome mientras completa el formulario. Se reinicia cada vez que toca
   la selección (agrega o quita una hora) y, si se acaba sin enviar, se
   libera sola. La reserva real y a prueba de dos pestañas la hace el
   servidor al guardar (ver hayConflicto en apps-script.gs); esto es el
   aviso al estudiante de que ese cupo no es suyo para siempre. */
const RESERVA_SEGUNDOS = 5 * 60;
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/* Redondea hacia arriba a la próxima hora en punto. Si ya cae justo en una
   hora en punto, se queda igual (es el mismo criterio que un Math.ceil). */
const techoHora = (d) => {
  const r = new Date(d);
  r.setMinutes(0, 0, 0);
  if (d.getTime() > r.getTime()) r.setHours(r.getHours() + 1);
  return r;
};

const generarId = () => (
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

/* Misma sesión (pestaña) para toda la duración de la reserva temporal, no
   solo mientras el modal de Agenda está montado: si el estudiante cierra el
   modal antes de enviar, la reserva sigue viva en el servidor (ver el
   comentario grande más abajo) y, si vuelve a abrirlo, necesita el MISMO
   sesionId para que el servidor reconozca esa reserva como propia — si le
   diéramos un id nuevo en cada apertura, su propio cupo le aparecería como
   "Reservado" por otra persona. sessionStorage sobrevive a que el
   componente se desmonte, pero no a que se cierre la pestaña — que es
   justo cuando la reserva ya no tiene sentido seguir viva del todo. */
function obtenerSesionId() {
  const CLAVE = "ayudate_sesion_reserva";
  try {
    let id = sessionStorage.getItem(CLAVE);
    if (!id) {
      id = generarId();
      sessionStorage.setItem(CLAVE, id);
    }
    return id;
  } catch {
    return generarId();   // modo privado o cuota llena: sigue funcionando, solo sin persistir
  }
}

/* Popup de "cómo quiere continuar", entre el paso 3 y las políticas, solo
   para quien todavía no tiene sesión (ver sesionActiva en Agenda más abajo).
   Reutiliza la misma cáscara de modal chico que AvisoPoliticas (Politicas.jsx:
   "mdl avw" + avh/avicon/avb) para que las dos ventanas emergentes de este
   mismo flujo se vean como parte de un mismo sistema. */
function PromptAuth({ onIniciarSesion, onCrearCuenta, onInvitado, onCerrar }) {
  return (
    <Capa onCerrar={onCerrar} alta etiqueta="¿Cómo quiere continuar?">
      <div className="mdl avw" aria-labelledby="pa-t" aria-describedby="pa-d">
        <div className="avh" style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div className="avicon" aria-hidden="true">?</div>
            <h4 id="pa-t">¿Ya tiene cuenta con nosotros?</h4>
          </div>
          {/* Cerrar acá solo descarta el popup, sin decidir nada: el
              estudiante se queda en el paso 3 del formulario y, si vuelve a
              tocar "Enviar solicitud", este mismo popup reaparece hasta que
              elija una de las tres opciones de abajo. */}
          <button type="button" className="x" onClick={onCerrar}
            aria-label="Cerrar" title="Cerrar">×</button>
        </div>
        <div className="avb">
          <p className="avtexto" id="pa-d">
            Inicie sesión o cree una cuenta para ver esta solicitud después desde su propio
            panel de estudiante. También puede continuar sin cuenta: le avisamos todo por
            correo igual que siempre.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button type="button" className="btn btn-azul" style={{ width: "100%" }}
              onClick={onIniciarSesion}>
              Iniciar sesión
            </button>
            <button type="button" className="btn btn-p" style={{ width: "100%" }}
              onClick={onCrearCuenta}>
              Crear cuenta
            </button>
            <button type="button" className="btn" style={{ width: "100%" }}
              onClick={onInvitado}>
              Continuar como invitado
            </button>
          </div>
        </div>
      </div>
    </Capa>
  );
}

/* perfilInicial (opcional): datos de un estudiante ya logueado (ver
   DashboardEstudiante.jsx), para precargar nombre/correo/teléfono en vez de
   arrancar los tres campos vacíos — la persona igual puede editarlos antes
   de enviar. telefono llega en el mismo formato que ya arma este mismo
   formulario al enviar ("codigoTel tel", ver el submit más abajo), así que
   separarlo en el primer espacio recupera las dos partes originales. */
export function Agenda({ tutor, onClose, perfilInicial }) {
  const ahora = new Date();
  /* Nada se puede reservar dentro de las próximas 24 horas: el tutor tiene
     que poder confirmar y el estudiante alcanzar a pagar. Encima de esas 24
     horas, se redondea hacia arriba a la hora en punto siguiente: si son
     las 13:05 del 19 de agosto, el límite no queda en las 13:05 del 20
     (nadie reserva a esa hora exacta), sino en las 14:00 del 20 — la
     primera hora en punto que ya cumple las 24 horas completas. */
  const limite = techoHora(new Date(ahora.getTime() + HORAS_MINIMAS * 36e5));
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const primerDia = new Date(limite); primerDia.setHours(0, 0, 0, 0);
  /* Ventana de un mes: nada se puede elegir más allá de este día, sin
     importar cuánto horario tenga marcado el tutor más adelante. Mismo tope
     que revalida crearSolicitud() en apps-script.gs. */
  const ultimoDia = new Date(hoy); ultimoDia.setDate(ultimoDia.getDate() + DIAS_MAXIMO_RESERVA);
  const [mes, setMes] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const [fecha, setFecha] = useState(null);       // día que se está editando
  const [sel, setSel] = useState({});             // { "2026-08-14": [9, 10, 14], ... }
  const telInicial = (perfilInicial?.telefono || "").trim().split(/\s+/);
  const [nombre, setNombre] = useState(perfilInicial?.nombre || "");
  const [correo, setCorreo] = useState(perfilInicial?.correo || "");
  const [codigoTel, setCodigoTel] = useState(telInicial[0] || CODIGO_PAIS_PREDETERMINADO);
  const [tel, setTel] = useState(telInicial.slice(1).join(" ") || "");
  const [nota, setNota] = useState("");
  const [error, setError] = useState("");
  const [intento, setIntento] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [ok, setOk] = useState(false);
  const [aviso, setAviso] = useState(false);
  const [verPol, setVerPol] = useState(false);
  /* Sesión ya activa (ver DashboardEstudiante.jsx, mismo criterio): si hay
     token guardado, la persona ya se identificó alguna vez y no tiene
     sentido volver a preguntarle cómo quiere continuar. Se lee una sola
     vez al montar, no en cada render — perfilInicial ya llega precargado
     desde esa misma sesión (ver CatalogoTutores.jsx). */
  const [sesionActiva] = useState(() => !!leerSesionEstudiante());
  /* Popup de "cómo quiere continuar" para quien NO tiene sesión: se
     muestra una sola vez por intento de envío. Una vez que elige "Crear
     cuenta" o "Continuar como invitado", decisionAuthTomada queda en true
     y el siguiente click en "Enviar solicitud" ya no vuelve a preguntarle
     (si no, elegir "Crear cuenta" y volver a tocar el botón la
     encerraría en el mismo popup una y otra vez). */
  const [mostrarAuthPrompt, setMostrarAuthPrompt] = useState(false);
  const [decisionAuthTomada, setDecisionAuthTomada] = useState(false);
  const cuerpoRef = React.useRef(null);
  const cabeceraRef = React.useRef(null);
  const horasRef = React.useRef(null);

  /* Identifica esta pestaña/sesión ante el servidor, para que la reserva
     temporal de OTRO estudiante bloquee acá, pero la propia selección no se
     bloquee a sí misma (ver reservarTemporal/getOcupacion en api.js). Estable
     entre aperturas y cierres del modal — ver obtenerSesionId() arriba. */
  const [sesionId] = useState(obtenerSesionId);
  /* Bloques que ya no se pueden elegir: { ocupado, reservadoTemporal }, cada
     uno con la misma forma que "sel" ({ "2026-08-20": [14, 15], ... }).
     ocupado = solicitud viva de alguien (pendiente, confirmada o pagada).
     reservadoTemporal = otro estudiante los tiene elegidos ahora mismo, sin
     haber enviado todavía. Las dos deshabilitan el botón por igual. */
  const [ocupacion, setOcupacion] = useState({ ocupado: {}, reservadoTemporal: {} });

  const ac = infoCurso(tutor.curso).color;
  const curso = tutor.curso;
  const { cliente } = tarifas(tutor);
  const disp = tutor.disponibilidad || {};

  /* Clave estable por día, sin depender de zona horaria */
  const clave = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
    + `-${String(d.getDate()).padStart(2, "0")}`;
  const desdeClave = (k) => { const [a, m, d] = k.split("-").map(Number); return new Date(a, m - 1, d); };

  /* true si esa hora, en esa fecha, ya tiene una solicitud viva de alguien
     o la reserva temporal de OTRO estudiante — en cualquiera de los dos
     casos, el bloque no se puede clickear. */
  const horaReservada = (d, h) => {
    const k = clave(d);
    return (ocupacion.ocupado[k] || []).includes(h)
      || (ocupacion.reservadoTemporal[k] || []).includes(h);
  };

  const horasSel = fecha ? (sel[clave(fecha)] || []) : [];
  const diasElegidos = Object.keys(sel).filter((k) => sel[k].length > 0).sort();
  const n = diasElegidos.reduce((t, k) => t + sel[k].length, 0);   // total de horas
  const totalCliente = cliente * n;
  const devolucion80 = r100(totalCliente * 0.8);
  const devolucion60 = r100(totalCliente * 0.6);

  const alternar = (h) => {
    if (!fecha) return;
    if (horaReservada(fecha, h)) return;   // ya se marca como no-clickeable, esto es solo un refuerzo
    const k = clave(fecha);
    setSel((prev) => {
      const actuales = prev[k] || [];
      const nuevas = actuales.includes(h)
        ? actuales.filter((x) => x !== h)
        : [...actuales, h].sort((a, b) => a - b);
      const copia = { ...prev };
      if (nuevas.length) copia[k] = nuevas; else delete copia[k];
      return copia;
    });
  };

  const quitarDia = (k) => setSel((prev) => {
    const copia = { ...prev }; delete copia[k]; return copia;
  });

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  /* Reinicia la reserva temporal cada vez que cambia la selección. Sin
     horas elegidas no hay nada que reservar. */
  const [restante, setRestante] = useState(null);
  const [expirado, setExpirado] = useState(false);
  useEffect(() => {
    const hay = Object.values(sel).some((h) => h.length > 0);
    setRestante(hay ? RESERVA_SEGUNDOS : null);
  }, [sel]);

  /* Ocupación en vivo: se pide al abrir la agenda y se refresca cada 20 s
     mientras esté abierta, para que lo que OTRO estudiante reserva o paga
     mientras este formulario sigue abierto también quede deshabilitado acá
     sin necesidad de recargar la página. */
  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      api.getOcupacion(tutor.id, curso, sesionId)
        .then((r) => { if (vivo) setOcupacion(r); })
        .catch(() => {});   // si falla, hayConflicto() en el servidor sigue protegiendo al enviar
    };
    cargar();
    const id = setInterval(cargar, 20000);
    return () => { vivo = false; clearInterval(id); };
  }, [tutor.id, curso, sesionId]);

  /* Avisa al servidor cuál es la selección actual de ESTA sesión, para que
     bloquee esas horas en el calendario de los demás por HOLD_SEGUNDOS
     (apps-script.gs). Se renueva cada vez que cambia la selección y, de
     paso, cada minuto mientras siga habiendo algo elegido — así una reserva
     que el usuario no toca (p. ej. mientras llena el resto del formulario)
     no se le vence del lado del servidor antes que el contador local.

     A PROPÓSITO no manda una liberación solo por montarse con "sel" vacío
     (que es como arranca siempre el modal, incluso reabierto): si el
     estudiante cerró el modal con horas elegidas, esa reserva sigue viva
     en el servidor — ver el efecto de más abajo — y el modal, al
     reabrirse, no la conoce todavía (sel vuelve a arrancar vacío). Mandar
     acá una liberación automática por el simple hecho de montarse borraría
     esa reserva bajo los pies del propio estudiante. Solo se libera cuando
     ESTE montaje pasa de tener algo elegido a no tener nada — deseleccionar
     a mano, "Limpiar todo", o que la cuenta regresiva llegue a 0 —, lo cual
     rastrea teniaAlgoRef. */
  const teniaAlgoRef = React.useRef(false);
  useEffect(() => {
    const hay = Object.values(sel).some((h) => h.length > 0);

    if (hay) {
      teniaAlgoRef.current = true;
      api.reservarTemporal(tutor.id, curso, sel, sesionId).catch(() => {});
      const id = setInterval(() => {
        api.reservarTemporal(tutor.id, curso, sel, sesionId).catch(() => {});
      }, 60000);
      return () => clearInterval(id);
    }

    if (teniaAlgoRef.current) {
      teniaAlgoRef.current = false;
      api.reservarTemporal(tutor.id, curso, {}, sesionId).catch(() => {});
    }
    return undefined;
  }, [sel, tutor.id, curso, sesionId]);

  /* A propósito NO hay un efecto que libere la reserva al desmontarse (al
     cerrar el modal con el botón ×, Escape o un click afuera): la reserva
     temporal debe seguir corriendo en el servidor durante los HOLD_SEGUNDOS
     que le queden desde la última renovación de arriba, igual que si el
     modal siguiera abierto — así nadie más puede tomar ese horario mientras
     el estudiante, por ejemplo, revisa algo en otra pestaña y vuelve a
     terminar el envío. Se libera sola con el TTL si los 5 minutos se
     cumplen sin que haya enviado la solicitud, o de una vez si sí la envía
     (ver el "if (d.sesionId) reservarTemporal(...)" al final de
     crearSolicitud() en apps-script.gs). */

  /* Cuenta regresiva. Al llegar a 0 se libera el horario y se saca al
     estudiante del flujo de una vez: no lo dejamos parado sobre un
     formulario cuyo horario ya no le pertenece, hay que volver a elegir
     desde cero. Se pausa mientras hay un envío en curso (enviando) y
     también mientras está abierto el aviso de políticas (aviso): sin esto,
     si alguien se tarda más de 5 minutos leyendo las políticas antes de
     tocar "Aceptar", el timeout limpiaba `sel` por debajo del diálogo
     todavía abierto y el clic en "Aceptar" terminaba mandando una
     solicitud vacía. */
  useEffect(() => {
    if (restante === null || ok || enviando || aviso) return;
    if (restante <= 0) {
      setSel({});
      setFecha(null);
      setNombre(perfilInicial?.nombre || "");
      setCorreo(perfilInicial?.correo || "");
      setCodigoTel(telInicial[0] || CODIGO_PAIS_PREDETERMINADO);
      setTel(telInicial.slice(1).join(" ") || "");
      setNota("");
      setRestante(null);
      setAviso(false);
      setExpirado(true);
      return;
    }
    const id = setTimeout(() => setRestante((r) => (r === null ? null : r - 1)), 1000);
    return () => clearTimeout(id);
  }, [restante, ok, enviando, aviso]);

  /* Al marcar un día, bajamos directo al paso 2 (las horas de ese día), justo
     debajo de la cabecera fija: con scrollIntoView a secas queda tapado. */
  useEffect(() => {
    if (!fecha) return;
    const id = setTimeout(() => {
      const el = horasRef.current;
      const contenedor = cuerpoRef.current?.closest(".ov");
      if (!el || !contenedor) return;
      const alturaCabecera = cabeceraRef.current?.offsetHeight || 0;
      const destino = el.getBoundingClientRect().top - contenedor.getBoundingClientRect().top
        + contenedor.scrollTop - alturaCabecera - 12;
      contenedor.scrollTo({ top: Math.max(destino, 0), behavior: "smooth" });
    }, 40);
    return () => clearTimeout(id);
  }, [fecha]);

  /* Días de la semana en que este tutor tiene algo marcado */
  const diasActivos = useMemo(() => {
    const set = new Set();
    Object.keys(disp).forEach((k) => set.add(Number(k.split("-")[0])));
    return set;
  }, [disp]);

  /* Horas disponibles para la fecha elegida */
  const horasDia = useMemo(() => {
    if (!fecha) return [];
    const d = fecha.getDay();
    return HORAS.filter((h) => {
      if (!disp[`${d}-${h}`]) return false;
      const cuando = new Date(fecha); cuando.setHours(h, 0, 0, 0);
      return cuando >= limite;     // deja fuera lo que caiga dentro de las 24 h
    });
  }, [fecha, disp]);

  /* Celdas del mes visible */
  const celdas = useMemo(() => {
    const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const dias = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
    const offset = (primero.getDay() + 6) % 7;          // lunes primero
    const out = Array(offset).fill(null);
    for (let i = 1; i <= dias; i++) out.push(new Date(mes.getFullYear(), mes.getMonth(), i));
    return out;
  }, [mes]);

  const mismoDia = (a, b) => a && b && a.toDateString() === b.toDateString();
  const mover = (d) => setMes(new Date(mes.getFullYear(), mes.getMonth() + d, 1));
  const puedeAvanzarMes = mes.getFullYear() < ultimoDia.getFullYear()
    || (mes.getFullYear() === ultimoDia.getFullYear() && mes.getMonth() < ultimoDia.getMonth());
  const NOM_MES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
    "agosto", "setiembre", "octubre", "noviembre", "diciembre"];

  const msg = {
    hora: n === 0
      ? "Elija un día en el calendario y marque al menos una hora."
      : "",
    nombre: vNombre(nombre),
    correo: vCorreo(correo),
    telefono: vTelefono(tel),
    nota: vTexto(nota, 10, "Cuéntenos qué necesita repasar."),
  };
  const falta = Object.fromEntries(Object.entries(msg).map(([k, v]) => [k, !!v]));
  const completo = !Object.values(falta).some(Boolean);
  /* Caracteres no permitidos se avisan al toque, sin esperar al intento de envío. */
  const vivo = {
    nombre: /[^A-Za-zÀ-ÿ\s']/.test(nombre),
    telefono: /\D/.test(tel),
  };
  const cl = (k) => (vivo[k] || (intento && falta[k]) ? "falta" : "");
  const ay = (k) => ({ error: msg[k], mostrar: vivo[k] || intento });
  /* La lista se recalcula sola: va desapareciendo conforme se completan campos */
  const pendientes = intento ? Object.values(msg).filter(Boolean) : [];
  const hayDatos = n > 0 || nombre.trim() || correo.trim() || tel.trim() || nota.trim();

  const enviar = () => {
    if (!completo) {
      setIntento(true);
      setTimeout(() => {
        const el = cuerpoRef.current?.querySelector(".falta, .falta-bloque");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el?.focus) el.focus({ preventScroll: true });
      }, 40);
      return;
    }
    /* Con sesión ya activa no hay nada que preguntar: directo a políticas.
       Sin sesión, se pregunta una sola vez (ver decisionAuthTomada arriba). */
    if (!sesionActiva && !decisionAuthTomada) {
      setMostrarAuthPrompt(true);
      return;
    }
    setError("");
    setAviso(true);
  };

  const continuarComoInvitado = () => {
    setMostrarAuthPrompt(false);
    setDecisionAuthTomada(true);
    setError("");
    setAviso(true);
  };

  return (
    <Capa onCerrar={onClose} etiqueta={`Solicitar tutoría con ${tutor.nom}`}
      confirmarSalida={!ok && !expirado && hayDatos}
      mensajeSalida="Va a perder los horarios y los datos que llenó. ¿Cerrar de todas formas?">
      {mostrarAuthPrompt && (
        <PromptAuth
          onIniciarSesion={() => irA_ruta("/login")}
          onCrearCuenta={() => irA_ruta("/crear-cuenta-estudiante")}
          onInvitado={continuarComoInvitado}
          onCerrar={() => setMostrarAuthPrompt(false)}
        />
      )}
      {aviso && <AvisoPoliticas
        onAceptar={async () => {
          setAviso(false); setEnviando(true); setError("");
          try {
            await api.crearSolicitud({
              tutor: tutor.id, curso, sel, nombre, correo,
              tel: `${codigoTel} ${tel}`, nota, sesionId,
            });
            setOk(true);
            setRestante(null);
            /* El servidor ya liberó la reserva temporal de esta sesión
               (crearSolicitud() en apps-script.gs) porque la solicitud real
               ya la reemplaza. Se limpia sel también acá para que el
               heartbeat de arriba no la vuelva a crear por las dudas
               mientras la pantalla de "enviada" sigue montada. */
            setSel({});
          } catch (err) {
            if (err?.codigo === "HORARIO_OCUPADO") {
              /* Caso raro: alguien más lo tomó justo entre la última
                 actualización de ocupación (cada 20 s) y este envío. Se
                 refresca de una vez para que el bloque quede marcado como
                 reservado sin esperar al próximo ciclo. */
              setSel({}); setFecha(null); setRestante(null);
              setError([err.message]);
              api.getOcupacion(tutor.id, curso, sesionId).then(setOcupacion).catch(() => {});
            } else {
              setError([err?.message
                || "No pudimos enviar la solicitud. Revise su conexión y pruebe de nuevo; "
                  + "lo que llenó sigue acá."]);
            }
          } finally { setEnviando(false); }
        }}
        onCerrar={() => setAviso(false)} />}
      {verPol && <PanelPoliticas onClose={() => setVerPol(false)} />}
      <div className="mdl" style={{ "--ac": ac }}>
        <div className="mdl-h" ref={cabeceraRef}>
          <div>
            <div style={{ display: "flex", gap: 13, alignItems: "center" }}>
              <Avatar tutor={tutor} size={42} />
              <div className="anton" style={{ fontSize: 25 }}>{tutor.nom}</div>
            </div>
            <div className="mt" style={{ marginTop: 4 }}>
              <span className="curso-resaltado">{infoCurso(curso).nom}</span>
              {tutor.nivel && /^[ABC][12]$/.test(tutor.nivel) ? ` · nivel ${tutor.nivel}` : ""}
              {" · "}{tutor.carrera}
            </div>
          </div>
          <button className="x" onClick={onClose} aria-label="Cerrar sin enviar la solicitud"
            title="Cerrar">×</button>
        </div>
        <div className="mdl-b" ref={cuerpoRef}>
          {ok ? (
            <div className="done">
              <b>Solicitud enviada.</b> Le acabamos de escribir a {tutor.nom} para que confirme
              si puede a esa hora.
              <ol className="flujo">
                <li><b>Ahora:</b> el tutor recibe su solicitud y responde desde su correo.</li>
                <li><b>Si confirma:</b> le llega un correo con los datos para pagar por SINPE
                  Móvil. Ahí queda apartada en firme.</li>
                <li><b>Si no puede:</b> le avisamos para que elija otro horario u otro tutor.
                  No se le cobra nada.</li>
              </ol>
              <span className="flujonota">Revise su correo, incluido el spam.</span>
            </div>
          ) : expirado ? (
            <div className="expirado">
              <b>Se acabó el tiempo de la reserva.</b> Pasaron los 5 minutos y liberamos las
              horas que había elegido para que otro estudiante pueda tomarlas.
              <p style={{ marginTop: 14, marginBottom: 0 }}>
                Si todavía quiere esa tutoría con {tutor.nom}, cierre esta ventana y <b>vuelva a
                abrir la agenda</b> para elegir el horario de nuevo.
              </p>
              <button className="btn btn-p" style={{ width: "100%", marginTop: 18 }}
                onClick={onClose}>
                Entendido, cerrar
              </button>
            </div>
          ) : (
            <>
              <div className="notas" style={{ marginBottom: 18 }}>
                <p><b>Cómo son las sesiones:</b> Las tutorías son 100% virtuales por videollamada
                  en Ayudate desde el navegador. Confirmada y pagada la sesión, recibirá el
                  enlace por correo; la sala se abre 5 minutos antes y cierra 3 minutos después
                  de terminar.</p>
                <p><b>Reservas:</b> Con al menos 24 horas de anticipación y un máximo de 1 mes
                  (hasta el {ultimoDia.getDate()} de {NOM_MES[ultimoDia.getMonth()]}). Cada
                  solicitud es para un único día; marque las horas deseadas.</p>
              </div>

              <div className="eye" style={{ marginBottom: 16 }}>1 · Elija el día</div>

              <div className="calh">
                <button className="cnav" onClick={() => mover(-1)} aria-label="Mes anterior">‹</button>
                <span>{NOM_MES[mes.getMonth()]} {mes.getFullYear()}</span>
                <button className="cnav" onClick={() => mover(1)} disabled={!puedeAvanzarMes}
                  aria-label="Mes siguiente">›</button>
              </div>
              <div className={`calm ${intento && n === 0 ? "falta-bloque" : ""}`}>
                {["L", "M", "M", "J", "V", "S", "D"].map((d, i) =>
                  <div className="cdd" key={i}>{d}</div>)}
                {celdas.map((d, i) => {
                  if (!d) return <div key={`v${i}`} />;
                  const pasado = d < primerDia;
                  const futuro = d > ultimoDia;
                  const hay = diasActivos.has(d.getDay());
                  const activo = mismoDia(d, fecha);
                  const cuantas = (sel[clave(d)] || []).length;
                  return (
                    <button key={i}
                      className={`cdia ${activo ? "on" : ""} ${cuantas ? "elegido" : ""} ${hay && !pasado && !futuro ? "hay" : ""}`}
                      disabled={pasado || futuro || !hay}
                      aria-label={`${d.getDate()} de ${NOM_MES[d.getMonth()]}`
                        + (pasado ? ", muy pronto para reservar"
                          : futuro ? ", fuera del mes de anticipación permitido"
                          : hay ? "" : ", sin disponibilidad")
                        + (cuantas ? `, ${cuantas} ${cuantas === 1 ? "hora elegida" : "horas elegidas"}` : "")}
                      onClick={() => {
                        if (activo) { setFecha(null); quitarDia(clave(d)); }
                        /* Una sola sesión por solicitud: elegir otro día reemplaza
                           las horas del día anterior en vez de sumarlas, así "sel"
                           nunca guarda más de una fecha a la vez (ver hayConflicto y
                           generarSalaJitsi en apps-script.gs, que asumen un solo día
                           por solicitud para no enredar la ventana de la sala). */
                        else { setSel({}); setFecha(d); }
                      }}>
                      {d.getDate()}
                      {cuantas > 0 && <i className="cnum">{cuantas}</i>}
                    </button>
                  );
                })}
              </div>

              {fecha && (
                <>
                  <div className="eye" style={{ margin: "26px 0 14px" }} ref={horasRef}>
                    2 · Horas del {fecha.getDate()} de {NOM_MES[fecha.getMonth()]}
                  </div>
                  {horasDia.length === 0 ? (
                    <p className="cleyenda">
                      Sin horas disponibles ese día. Puede ser que las que tiene marcadas caigan
                      dentro de las <b>próximas 24 horas</b>: pruebe con un día más adelante.
                    </p>
                  ) : (
                    <div className={`hchips ${intento && n === 0 ? "falta-bloque" : ""}`}>
                      {horasDia.map((h) => {
                        const on = horasSel.includes(h);
                        const reservado = horaReservada(fecha, h);
                        return (
                          <button key={h} className={`hchip ${on ? "on" : ""} ${reservado ? "reservado" : ""}`}
                            aria-pressed={on} disabled={reservado}
                            aria-label={`${hLabel(h)}${reservado ? ", reservado, no disponible" : ""}`}
                            onClick={() => alternar(h)}>
                            <b>{hLabel(h)}</b>
                            {reservado
                              ? <span className="hres">Reservado</span>
                              : <span className="hmodo virtual">virtual</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {restante !== null && (
                <div className="reserva-temp" role="status" aria-live="polite">
                  <span className="reserva-punto" aria-hidden="true" />
                  <span>
                    Horas bloqueadas temporalmente por <b>{mmss(restante)}</b> para que nadie
                    más las tome mientras completa la solicitud.
                  </span>
                </div>
              )}

              {diasElegidos.length > 0 && (
                <div className="resumen">
                  <div className="resh">
                    <span><b>{n} {n === 1 ? "hora" : "horas"}</b> elegidas</span>
                    <button type="button" onClick={() => setSel({})}>Limpiar todo</button>
                  </div>
                  <ul>
                    {diasElegidos.map((k) => {
                      const d = desdeClave(k);
                      return (
                        <li key={k}>
                          <span className="rfecha">
                            {DIAS.find((x) => x.k === d.getDay())?.lb} {d.getDate()} de{" "}
                            {NOM_MES[d.getMonth()]}
                          </span>
                          <span className="rhoras">{sel[k].map(hLabel).join(" · ")}</span>
                          <button type="button" onClick={() => quitarDia(k)}
                            aria-label={`Quitar el ${d.getDate()} de ${NOM_MES[d.getMonth()]}`}>×</button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="eye" style={{ margin: "28px 0 16px" }}>
                3 · Sus datos
              </div>
              <input className={`in ${cl("nombre")}`} placeholder="Nombre completo"
                aria-label="Nombre completo" aria-invalid={intento && falta.nombre}
                value={nombre} onChange={(e) => setNombre(e.target.value.replace(/-/g, ""))} />
              <Ayuda texto="Nombre y apellido." {...ay("nombre")} />

              <input className={`in ${cl("correo")}`} type="email" placeholder="Correo"
                aria-label="Correo" aria-invalid={intento && falta.correo}
                value={correo} onChange={(e) => setCorreo(e.target.value)} />
              <Ayuda texto="Ahí le escribimos para confirmar. Ej: nombre@dominio"
                {...ay("correo")} />
              <NotaCorreo />

              <TelefonoInput className={cl("telefono")} codigo={codigoTel} onCodigoChange={setCodigoTel}
                valor={tel} onValorChange={setTel} ariaInvalid={intento && falta.telefono}
                placeholder="Teléfono o WhatsApp" />
              <Ayuda texto="Solo dígitos, sin espacios ni guiones."
                {...ay("telefono")} />
              <textarea className={`in ta ${cl("nota")}`} rows={3} maxLength={220}
                placeholder="¿Qué temas necesita repasar? ¿Tiene parcial pronto?"
                aria-label="Qué necesita repasar" value={nota}
                onChange={(e) => setNota(e.target.value)} />
              <div className={`contador ${nota.trim().length < 10 ? "corto" : ""}`}>
                {nota.trim().length < 10
                  ? `${nota.trim().length} de 10 mínimo`
                  : `${nota.length}/220`}
              </div>
              <Ayuda texto="Mínimo 10 caracteres. Mientras más claro, mejor le coordinamos."
                {...ay("nota")} />

              <div className="recibo">
                <div className="row">
                  <span>Tarifa por hora</span>
                  <span>{colones(cliente)}</span>
                </div>
                {n > 0 && (
                  <div className="row">
                    <span>Horas solicitadas</span>
                    <span>× {n}</span>
                  </div>
                )}
                <div className="row tot">
                  <span>Total{n > 0 ? ` · ${n} ${n === 1 ? "hora" : "horas"}` : ""}</span>
                  <span>{colones(totalCliente)}</span>
                </div>
                {n === 0 && (
                  <p className="note" style={{ marginTop: 8 }}>
                    Elija las horas para ver el monto total.
                  </p>
                )}
                <p className="note">
                  Monto de referencia. <b>No se paga ahora:</b> primero el tutor confirma que puede
                  a esa hora, y recién ahí te llega el correo con los datos del SINPE.
                  {n > 0 && <> Cancelando con más de 24 horas se devuelve el <b>80%</b>{" "}
                    ({colones(devolucion80)}); con 24 horas o menos, el <b>60%</b>{" "}
                    ({colones(devolucion60)}).</>}
                </p>
              </div>

              {(pendientes.length > 0 || error) && (
                <ListaErrores error={error || pendientes} vivo={!error} />
              )}

              <button className="btn btn-p" style={{ width: "100%", marginTop: 22 }}
                onClick={enviar} disabled={enviando} aria-busy={enviando}>
                {enviando ? <><span className="spin" aria-hidden="true" />Enviando…</> : "Enviar solicitud"}
              </button>
              <p className="oblig">Todos los campos son obligatorios.</p>
              <p className="pie">
                Al enviar acepta las{" "}
                <button type="button" className="enlace-pol" onClick={() => setVerPol(true)}>
                  POLÍTICAS DE USO y las POLÍTICAS DE CANCELACIÓN Y DEVOLUCIÓN
                </button>.
              </p>
            </>
          )}
        </div>
      </div>
    </Capa>
  );
}
